import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { xhrRequest } from '../lib/xhrRequest';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Link as LinkIcon, Mail, Lock,
  CreditCard, CheckCircle, XCircle, Loader2, Home, List,
  LogOut, QrCode,
} from 'lucide-react';
import Footer from '../components/Footer';

// Small debounce hook — only used for the slug availability check.
function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function SettingsPage() {
  const { user, token, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Profile section
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [serviceTitle, setServiceTitle] = useState(user?.serviceTitle || '');
  const [profileSaving, setProfileSaving] = useState(false);

  // Slug section
  const [slug, setSlug] = useState(user?.publicSlug || '');
  const [slugState, setSlugState] = useState({ status: 'idle', reason: null });
  const [slugSaving, setSlugSaving] = useState(false);
  const debouncedSlug = useDebounced(slug.trim().toLowerCase(), 400);

  // Email section
  const [email, setEmail] = useState(user?.email || '');
  const [emailSaving, setEmailSaving] = useState(false);

  // Password section
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const slugAbortRef = useRef(0);

  useEffect(() => {
    if (!debouncedSlug || debouncedSlug === user?.publicSlug) {
      setSlugState({ status: 'idle', reason: null });
      return;
    }
    const gen = ++slugAbortRef.current;
    setSlugState({ status: 'checking', reason: null });
    (async () => {
      const { ok, data } = await xhrRequest(
        'GET',
        `${API}/users/check-slug?slug=${encodeURIComponent(debouncedSlug)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (gen !== slugAbortRef.current) return; // stale
      if (!ok) {
        setSlugState({ status: 'error', reason: data.detail || 'Check failed' });
        return;
      }
      setSlugState({
        status: data.available ? 'available' : 'taken',
        reason: data.reason || null,
      });
    })();
  }, [debouncedSlug, API, token, user?.publicSlug]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    const { ok, data } = await xhrRequest('PUT', `${API}/users/profile`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ displayName, serviceTitle }),
    });
    setProfileSaving(false);
    if (!ok) return toast.error(data.detail || 'Could not save profile');
    updateUser(data);
    toast.success('Profile updated');
  };

  const saveSlug = async (e) => {
    e.preventDefault();
    if (slugState.status === 'taken' || slugState.status === 'error') {
      return toast.error(slugState.reason || 'That URL is not available');
    }
    setSlugSaving(true);
    const { ok, data } = await xhrRequest('PUT', `${API}/users/profile`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ publicSlug: slug.trim().toLowerCase() }),
    });
    setSlugSaving(false);
    if (!ok) return toast.error(data.detail || 'Could not update URL');
    updateUser(data);
    toast.success('Your Golden Tips URL is updated');
  };

  const saveEmail = async (e) => {
    e.preventDefault();
    if (email.toLowerCase() === user?.email?.toLowerCase()) {
      return toast.info('That is already your email');
    }
    setEmailSaving(true);
    const { ok, data } = await xhrRequest('PUT', `${API}/users/profile`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email }),
    });
    setEmailSaving(false);
    if (!ok) return toast.error(data.detail || 'Could not update email');
    updateUser(data);
    toast.success('Email updated');
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      return toast.error('New password must be at least 6 characters');
    }
    setPwSaving(true);
    const { ok, data } = await xhrRequest('POST', `${API}/users/change-password`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    setPwSaving(false);
    if (!ok) return toast.error(data.detail || 'Could not change password');
    setCurrentPassword('');
    setNewPassword('');
    toast.success('Password changed');
  };

  const slugHint = () => {
    if (!slug || slug === user?.publicSlug) return null;
    if (slugState.status === 'checking') {
      return (
        <span className="text-xs text-[#A1A1AA] flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" /> Checking…
        </span>
      );
    }
    if (slugState.status === 'available') {
      return (
        <span className="text-xs text-green-400 flex items-center gap-1">
          <CheckCircle size={12} /> Available
        </span>
      );
    }
    if (slugState.status === 'taken') {
      return (
        <span className="text-xs text-red-400 flex items-center gap-1">
          <XCircle size={12} /> That URL is already taken
        </span>
      );
    }
    if (slugState.status === 'error') {
      return (
        <span className="text-xs text-red-400 flex items-center gap-1">
          <XCircle size={12} /> {slugState.reason}
        </span>
      );
    }
    return null;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="settings-page">
      {/* Mobile Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-b border-white/5 px-4 py-3 md:hidden">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-[#A1A1AA] hover:text-white" data-testid="settings-back-mobile">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-heading font-bold text-lg text-white">Settings</h1>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar — mirrors dashboard sidebar */}
        <aside className="hidden md:flex flex-col w-64 h-screen sticky top-0 bg-[#0a0a0a] border-r border-white/5 p-6">
          <div className="mb-8">
            <h1 className="font-heading font-bold text-xl text-[#F5C542]">Golden Tips</h1>
          </div>
          <nav className="flex-1 space-y-1">
            <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors">
              <Home size={20} /><span>Dashboard</span>
            </Link>
            <Link to="/dashboard/tips" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors">
              <List size={20} /><span>Tips History</span>
            </Link>
            <Link to="/dashboard/settings" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 text-white">
              <User size={20} /><span>Settings</span>
            </Link>
          </nav>
          <div className="pt-6 border-t border-white/5">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm"
              data-testid="settings-logout-button"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="hidden md:block mb-2">
              <h2 className="text-2xl md:text-3xl font-heading font-bold">Settings</h2>
              <p className="text-[#A1A1AA]">Manage your account and public page.</p>
            </div>

            {/* Payout status — link to resume onboarding if not connected */}
            <section className="stat-card" data-testid="settings-payout-status">
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                <CreditCard size={20} /> Payout status
              </h3>
              {user?.stripeOnboarded ? (
                <p className="text-sm text-green-300 flex items-center gap-2">
                  <CheckCircle size={16} /> Connected — you&apos;re set to receive tips.
                </p>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <p className="text-sm text-amber-200/90 flex-1">
                    {user?.stripeAccountId
                      ? "Onboarding started but not finished. You can't receive tips until this is done."
                      : "Payouts are not connected yet."}
                  </p>
                  <Link
                    to="/onboarding/payouts"
                    className="btn-primary !w-auto !px-4 !py-2 text-sm flex items-center justify-center gap-2"
                    data-testid="settings-manage-payouts"
                  >
                    {user?.stripeAccountId ? 'Continue setup' : 'Connect payouts'}
                  </Link>
                </div>
              )}
            </section>

            {/* Profile */}
            <section className="stat-card">
              <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                <User size={20} /> Profile
              </h3>
              <form onSubmit={saveProfile} className="space-y-4" data-testid="settings-profile-form">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B]">Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#F5C542] focus:outline-none"
                    data-testid="settings-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B]">What do you do?</label>
                  <input
                    type="text"
                    value={serviceTitle}
                    onChange={(e) => setServiceTitle(e.target.value)}
                    placeholder="Bartender, stylist, valet…"
                    className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#F5C542] focus:outline-none"
                    data-testid="settings-role-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={profileSaving}
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="settings-profile-save"
                >
                  {profileSaving ? <><Loader2 className="animate-spin" size={16} /> Saving…</> : 'Save profile'}
                </button>
              </form>
            </section>

            {/* Public URL / slug */}
            <section className="stat-card">
              <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                <LinkIcon size={20} /> Your public URL
              </h3>
              <form onSubmit={saveSlug} className="space-y-3" data-testid="settings-slug-form">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B]">Custom URL</label>
                  <div className="mt-1 flex items-center rounded-xl bg-white/5 border border-white/10 focus-within:border-[#F5C542] overflow-hidden">
                    <span className="pl-4 pr-1 text-[#A1A1AA] text-sm select-none">
                      {window.location.host}/u/
                    </span>
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) =>
                        setSlug(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase())
                      }
                      className="flex-1 px-1 py-3 bg-transparent text-white focus:outline-none font-mono"
                      minLength={3}
                      maxLength={30}
                      data-testid="settings-slug-input"
                    />
                  </div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <p className="text-xs text-[#52525B]">3–30 lowercase letters, numbers, hyphens.</p>
                    {slugHint()}
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={
                    slugSaving ||
                    !slug ||
                    slug === user?.publicSlug ||
                    slugState.status === 'checking' ||
                    slugState.status === 'taken' ||
                    slugState.status === 'error'
                  }
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="settings-slug-save"
                >
                  {slugSaving ? <><Loader2 className="animate-spin" size={16} /> Saving…</> : 'Update URL'}
                </button>
                <Link
                  to={`/u/${user?.publicSlug}`}
                  target="_blank"
                  className="text-xs text-[#A1A1AA] hover:text-white flex items-center gap-1 mt-2"
                >
                  <QrCode size={12} /> View my public page
                </Link>
              </form>
            </section>

            {/* Email */}
            <section className="stat-card">
              <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                <Mail size={20} /> Email
              </h3>
              <form onSubmit={saveEmail} className="space-y-3" data-testid="settings-email-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#F5C542] focus:outline-none"
                  data-testid="settings-email-input"
                />
                <button
                  type="submit"
                  disabled={emailSaving}
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="settings-email-save"
                >
                  {emailSaving ? <><Loader2 className="animate-spin" size={16} /> Saving…</> : 'Update email'}
                </button>
              </form>
            </section>

            {/* Password */}
            <section className="stat-card">
              <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                <Lock size={20} /> Password
              </h3>
              <form onSubmit={savePassword} className="space-y-3" data-testid="settings-password-form">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B]">Current password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#F5C542] focus:outline-none"
                    data-testid="settings-current-password-input"
                    autoComplete="current-password"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B]">New password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#F5C542] focus:outline-none"
                    data-testid="settings-new-password-input"
                    autoComplete="new-password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={pwSaving || !currentPassword || !newPassword}
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="settings-password-save"
                >
                  {pwSaving ? <><Loader2 className="animate-spin" size={16} /> Saving…</> : 'Change password'}
                </button>
              </form>
            </section>
          </div>
          <Footer className="mt-10 max-w-2xl mx-auto" />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/5 px-4 py-2 flex items-center justify-around z-50">
        <Link to="/dashboard" className="flex flex-col items-center gap-1 py-2 px-4 text-[#A1A1AA]">
          <Home size={20} /><span className="text-xs">Home</span>
        </Link>
        <Link to="/dashboard/tips" className="flex flex-col items-center gap-1 py-2 px-4 text-[#A1A1AA]">
          <List size={20} /><span className="text-xs">Tips</span>
        </Link>
        <Link to="/dashboard/settings" className="flex flex-col items-center gap-1 py-2 px-4 text-[#F5C542]">
          <User size={20} /><span className="text-xs">Settings</span>
        </Link>
      </nav>
    </div>
  );
}
